import React, { useState, useRef, useEffect } from 'react';
import { FaPhone, FaChevronDown } from 'react-icons/fa';

// Common country codes for North America and Nigeria
const countryCodes = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸', name: 'United States/Canada' },
  { code: '+234', country: 'NG', flag: '🇳🇬', name: 'Nigeria' },
  { code: '+44', country: 'GB', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'France' },
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Germany' },
  { code: '+91', country: 'IN', flag: '🇮🇳', name: 'India' },
  { code: '+86', country: 'CN', flag: '🇨🇳', name: 'China' },
  { code: '+81', country: 'JP', flag: '🇯🇵', name: 'Japan' },
  { code: '+61', country: 'AU', flag: '🇦🇺', name: 'Australia' },
  { code: '+27', country: 'ZA', flag: '🇿🇦', name: 'South Africa' },
];

const PhoneInput = ({ value, onChange, name, placeholder, required = false, className = '' }) => {
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]); // Default to US/CA
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const isInternalUpdate = useRef(false);

  // Parse existing value to extract country code and number
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    if (value) {
      // Check if value starts with a country code
      const matchedCountry = countryCodes.find(country => 
        value.startsWith(country.code)
      );
      
      if (matchedCountry) {
        const numberPart = value.substring(matchedCountry.code.length).trim();
        setSelectedCountry(matchedCountry);
        setPhoneNumber(numberPart);
      } else {
        // If no country code found, assume it's just the number
        setPhoneNumber(value);
      }
    } else {
      // Clear if value is empty
      setPhoneNumber('');
    }
  }, [value]);

  // Update parent component when phone number or country changes
  useEffect(() => {
    const fullPhoneNumber = selectedCountry.code + (phoneNumber || '');
    // Only update if the value has actually changed and it's not from external prop
    if (value !== fullPhoneNumber && onChange) {
      isInternalUpdate.current = true;
      onChange({
        target: {
          name: name,
          value: fullPhoneNumber
        }
      });
    }
  }, [selectedCountry, phoneNumber, name, onChange, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    inputRef.current?.focus();
  };

  const handlePhoneChange = (e) => {
    // Only allow numbers, spaces, hyphens, and parentheses
    const cleaned = e.target.value.replace(/[^\d\s\-()]/g, '');
    setPhoneNumber(cleaned);
  };

  const handleKeyDown = (e) => {
    // Allow backspace, delete, tab, escape, enter, and arrow keys
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode) ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        <span className="flex items-center gap-2">
          <FaPhone className="text-primary-600" />
          Phone Number
        </span>
      </label>
      <div className="flex rounded-xl border border-gray-300 focus-within:ring-2 focus-within:ring-primary-600 focus-within:border-transparent transition">
        {/* Country Code Selector */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 px-3 py-3 border-r border-gray-300 bg-gray-50 hover:bg-gray-100 transition rounded-l-xl focus:outline-none focus:ring-2 focus:ring-primary-600"
          >
            <span className="text-lg">{selectedCountry.flag}</span>
            <span className="text-sm font-medium text-gray-700">{selectedCountry.code}</span>
            <FaChevronDown className={`text-xs text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
              {countryCodes.map((country) => (
                <button
                  key={`${country.code}-${country.country}`}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 transition text-left ${
                    selectedCountry.code === country.code && selectedCountry.country === country.country
                      ? 'bg-primary-600/10'
                      : ''
                  }`}
                >
                  <span className="text-xl">{country.flag}</span>
                  <div className="flex-1">
                    <div className="font-medium text-gray-700">{country.name}</div>
                    <div className="text-sm text-gray-500">{country.code}</div>
                  </div>
                  {selectedCountry.code === country.code && selectedCountry.country === country.country && (
                    <span className="text-primary-600">✓</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <input
          ref={inputRef}
          type="tel"
          name={name}
          value={phoneNumber}
          onChange={handlePhoneChange}
          onKeyDown={handleKeyDown}
          className="flex-1 px-3 py-3 rounded-r-xl focus:outline-none"
          placeholder={placeholder || "Enter phone number"}
          required={required}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Selected: {selectedCountry.code} {phoneNumber || 'your number'}
      </p>
    </div>
  );
};

export default PhoneInput;

